import React, { useState, useEffect, useRef, useCallback } from 'react';
import { API, graphqlOperation } from 'aws-amplify';
import { getOrganizationWithDetails } from '../graphql/custom-queries';

import { Container, Row, Col, Card, Alert, Spinner, Form, Button, InputGroup, ListGroup, Modal, Dropdown, Image, Badge } from 'react-bootstrap';
import { useOrganization } from '../contexts/OrganizationContext';
import { useUser } from '../contexts/UserContext';
import { isOrgAdmin, hasChatAccess } from '../utils/orgAccess';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPaperPlane, 
  faRobot, 
  faUser, 
  faPlus, 
  faTrash, 
  faComments, 
  faEllipsisV,
  faSearch,
  faInfoCircle,
  faCog,
  faWallet,
  faArrowDown
} from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import './ChatBot.css';
import ReactMarkdown from 'react-markdown';
import AiIcon from '../assets/Ai-icon.png';
import { useNavigate } from 'react-router-dom';
import CHAT_API_URL from '../utils/chatApiUrl';
import LegacyDataBadge from './shared/LegacyDataBadge';

// Get API configuration from environment variables
const API_BASE_URL = CHAT_API_URL;
const API_KEY = process.env.REACT_APP_CHAT_API_KEY || 'test-api-key-123';

// For debugging during development
if (process.env.NODE_ENV === 'development') {
  console.log('API_BASE_URL:', API_BASE_URL);
}

// ── Helper: ensure user and organization exist in chatbot backend ──
const ensureChatBackendSync = async (user, activeOrganization) => {
  const userSub = user?.attributes?.sub;
  const email = user?.attributes?.email;
  const name = user?.attributes?.name || user?.attributes?.email || 'LeanFitt User';
  const orgId = activeOrganization?.id;
  if (!userSub || !orgId) return;

  try {
    // Idempotent user creation
    await fetch(`${API_BASE_URL}/users`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-API-Key': API_KEY },
      body: JSON.stringify({
        user_id: userSub,
        name,
        email,
        lf_user_id: userSub,
      }),
    });

    // Idempotent organization creation
    await fetch(`${API_BASE_URL}/organizations`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-API-Key': API_KEY },
      body: JSON.stringify({
        organization_id: orgId,
        stripe_id: '',
        source: 'LeanFITT',
        available_balance: 5,
      }),
    });
  } catch (err) {
    console.error('Error syncing with chat backend:', err);
  }
};

// ── Helper: parse SSE stream from fetch response ──
const parseSSEStream = async (response, onDelta, onDone, onError) => {
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || ''; // keep incomplete line in buffer

      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed || !trimmed.startsWith('data: ')) continue;

        try {
          const data = JSON.parse(trimmed.slice(6));
          if (data.type === 'delta') {
            onDelta(data.text);
          } else if (data.type === 'done') {
            onDone(data);
          }
        } catch {
          // skip malformed JSON lines
        }
      }
    }
  } catch (err) {
    onError(err);
  }
};

const ChatBot = () => {
  const { activeOrganization, organizations } = useOrganization();
  const { user } = useUser();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [organizationData, setOrganizationData] = useState(null);
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState([]);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [chatRooms, setChatRooms] = useState([]);
  const [selectedChatRoom, setSelectedChatRoom] = useState(null);
  const [showNewChatModal, setShowNewChatModal] = useState(false);
  const [newChatName, setNewChatName] = useState('');
  const [loadingChatRooms, setLoadingChatRooms] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [hasPermission, setHasPermission] = useState(true); // Default to true, will be checked
  const [checkingPermission, setCheckingPermission] = useState(true);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const autoScrollEnabled = useRef(true);
  // Same signal as autoScrollEnabled, but as state so the pill can render.
  const [atBottom, setAtBottom] = useState(true);



  // Balance & billing states
  const [balance, setBalance] = useState(null);
  const [loadingBalance, setLoadingBalance] = useState(false);
  const [backendSynced, setBackendSynced] = useState(false);
  const abortControllerRef = useRef(null);

  // ── Fetch balance from chatbot backend ──
  const fetchBalance = useCallback(async () => {
    if (!activeOrganization?.id) return;
    setLoadingBalance(true);
    try {
      // Read the org record — the SAME source Navigation and AI Settings use — so the chat
      // header shows a consistent balance. The former singular /available-balance endpoint
      // could lag and return 0 right after the fire-and-forget backend sync, leaving the
      // header stuck at $0.00 until the user navigated away and back.
      const res = await fetch(
        `${API_BASE_URL}/organizations/${encodeURIComponent(activeOrganization.id)}`,
        { headers: { 'X-API-Key': API_KEY } }
      );
      if (res.ok) {
        const data = await res.json();
        const value = data.organization?.available_balance ?? data.balance;
        if (value != null) setBalance(value);
      }
    } catch (err) {
      console.error('Error fetching balance:', err);
    } finally {
      setLoadingBalance(false);
    }
  }, [activeOrganization]);

  // ── Sync user & org with chatbot backend on mount ──
  useEffect(() => {
    if (user?.attributes?.sub && activeOrganization?.id && !backendSynced) {
      ensureChatBackendSync(user, activeOrganization).then(() => {
        setBackendSynced(true);
        fetchBalance();
      });
    }
  }, [user, activeOrganization, backendSynced, fetchBalance]);

  // Fetch organization data when org changes
  useEffect(() => {
    if (activeOrganization?.id) {
      fetchOrganizationData(activeOrganization.id);
    }
  }, [activeOrganization]);

  // Fetch chat rooms when user or org changes
  useEffect(() => {
    if (user?.attributes?.sub && activeOrganization?.id) {
      checkUserPermission();
      fetchChatRooms();
    }
  }, [user, activeOrganization]);

  // Keep the header balance live when it changes elsewhere (e.g. a top-up in
  // AI Settings) without a manual page refresh. The event carries the new
  // balance; fall back to a refetch if it doesn't.
  useEffect(() => {
    const onBalance = (e) => {
      if (e?.detail?.balance != null) {
        setBalance(e.detail.balance);
      } else {
        fetchBalance();
      }
    };
    window.addEventListener('ai-balance-updated', onBalance);
    return () => window.removeEventListener('ai-balance-updated', onBalance);
  }, [fetchBalance]);

  // Auto-scroll to bottom when messages change — only while the user is
  // parked at the bottom. Streaming updates `messages` on every delta, so an
  // ungated scroll here yanks the view down mid-read.
  useEffect(() => {
    if (messages.length === 0 || !autoScrollEnabled.current) return;

    const scrollToBottom = () => {
      const container = messagesContainerRef.current;
      if (!container || !autoScrollEnabled.current) return;
      container.scrollTop = container.scrollHeight;
    };

    scrollToBottom();
    const timers = [100, 300, 500].map(ms => setTimeout(scrollToBottom, ms));
    return () => timers.forEach(clearTimeout);
  }, [messages]);

  // Additional effect specifically for initial load after page refresh
  useEffect(() => {
    if (!loadingMessages && selectedChatRoom) {
      // This runs after messages are loaded and loading is complete
      const forceScrollToBottom = () => {
        const container = messagesContainerRef.current;
        if (container) {
          autoScrollEnabled.current = true;
          setAtBottom(true);
          container.scrollTop = container.scrollHeight;
        }
        
        if (messagesEndRef.current) {
          messagesEndRef.current.scrollIntoView({ behavior: 'auto', block: 'end' });
        }
      };

      // Multiple attempts with longer delays for initial load
      setTimeout(forceScrollToBottom, 200);
      setTimeout(forceScrollToBottom, 500);
      setTimeout(forceScrollToBottom, 1000);
    }
  }, [loadingMessages, selectedChatRoom]);

  // Check if user has permission to use chat.
  // Access = owner / co-owner OR a member not on the org's aiDisabledUsers
  // denylist — the same rule the admin UI uses to grant access. We no longer
  // require an ACTIVE OrganizationMember lookup: that filtered scan + strict
  // status check wrongly blocked enabled members (see utils/orgAccess.js).
  const checkUserPermission = () => {
    setCheckingPermission(true);
    setHasPermission(
      hasChatAccess(activeOrganization, user?.attributes?.sub, user?.attributes?.email)
    );
    setCheckingPermission(false);
  };

  // Track whether the user is parked at the bottom. Bound via onScroll on the
  // container (it mounts conditionally, so a mount-time addEventListener ran
  // against a null ref and never attached).
  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    const isAtBottom = scrollTop + clientHeight >= scrollHeight - 40;
    autoScrollEnabled.current = isAtBottom;
    setAtBottom(isAtBottom);
  };

  const scrollToBottom = (force = false) => {
    const container = messagesContainerRef.current;
    if (!container) return;

    // Enable auto-scroll and scroll to bottom
    autoScrollEnabled.current = true;
    setAtBottom(true);
    
    if (force) {
      // Immediate scroll
      container.scrollTop = container.scrollHeight;
    } else {
      // Smooth scroll
      container.scrollTo({
        top: container.scrollHeight,
        behavior: 'smooth'
      });
    }
  };

  const fetchOrganizationData = async (id) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await API.graphql(
        graphqlOperation(getOrganizationWithDetails, { id })
      );
      
      const data = response.data.getOrganization;
      setOrganizationData(data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching organization data:', err);
      setError('Failed to fetch organization data. Please try again later.');
      setLoading(false);
    }
  };



  const fetchChatRooms = async () => {
    if (!user?.attributes?.sub) return;
    
    setLoadingChatRooms(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/chat-rooms`, {
        // Scope the room list to the active org so a user's rooms in OTHER orgs don't show here.
        // Legacy rooms (no org yet) stay visible until first use stamps them (backend backfill).
        params: { user_id: user.attributes.sub, organization_id: activeOrganization?.id },
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': API_KEY
        }
      });
      
      // Sort chat rooms by last activity (most recent first)
      const sortedRooms = response.data.sort((a, b) => {
        const aLastActivity = new Date(a.last_activity || a.created_at || 0);
        const bLastActivity = new Date(b.last_activity || b.created_at || 0);
        return bLastActivity - aLastActivity;
      });
      
      setChatRooms(sortedRooms);

      // Re-select if nothing is selected OR the current selection isn't in this org's list
      // (e.g. after switching orgs — otherwise the previous org's conversation stays open).
      const selectionStillValid =
        selectedChatRoom && sortedRooms.some(room => room.id === selectedChatRoom.id);
      if (!selectionStillValid) {
        if (sortedRooms.length > 0) {
          setSelectedChatRoom(sortedRooms[0]);
          fetchChatMessages(sortedRooms[0].id);
        } else {
          setSelectedChatRoom(null);
          setMessages([]);
        }
      }
    } catch (err) {
      console.error('Error fetching chat rooms:', err);
      setError('Failed to load chat rooms. Please try again.');
    } finally {
      setLoadingChatRooms(false);
    }
  };

  const fetchChatMessages = async (chatRoomId) => {
    setLoadingMessages(true);
    setMessages([]);
    try {
      const response = await axios.get(`${API_BASE_URL}/chat-rooms/${chatRoomId}/messages`, {
        params: { user_id: user?.attributes?.sub },
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': API_KEY
        }
      });
      
      setMessages(response.data);
      
      // Enable auto-scroll and scroll to bottom after messages are loaded
      autoScrollEnabled.current = true;
      setTimeout(() => scrollToBottom(true), 100);
      
    } catch (err) {
      console.error('Error fetching chat messages:', err);
      setError('Failed to load chat history. Please try again.');
      setMessages([]);
    } finally {
      setLoadingMessages(false);
    }
  };

  const createChatRoom = async () => {
    if (!newChatName.trim() || !user?.attributes?.sub) return;
    
    try {
      const response = await axios.post(`${API_BASE_URL}/chat-rooms`, {
        name: newChatName,
        user_id: user.attributes.sub,
        organization_id: activeOrganization?.id
      }, {
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': API_KEY
        }
      });
      
      const newChatRoom = response.data;
      
      // Add to chat rooms and select it
      setChatRooms(prev => [...prev, newChatRoom]);
      setSelectedChatRoom(newChatRoom);
      setNewChatName('');
      setShowNewChatModal(false);
      
      // Clear messages since this is a new chat
      setMessages([]);
    } catch (err) {
      console.error('Error creating chat room:', err);
      setError('Failed to create chat room. Please try again.');
    }
  };

  const deleteChatRoom = async (chatRoomId) => {
    if (!chatRoomId) return;
    
    if (!window.confirm('Are you sure you want to delete this chat room? All messages will be permanently deleted.')) {
      return;
    }
    
    try {
      await axios.delete(`${API_BASE_URL}/chat-rooms/${chatRoomId}`, {
        params: { user_id: user?.attributes?.sub },
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': API_KEY
        }
      });
      
      // Remove from chat rooms list
      setChatRooms(prev => prev.filter(room => room.id !== chatRoomId));
      
      // If the deleted room was selected, clear selection
      if (selectedChatRoom?.id === chatRoomId) {
        setSelectedChatRoom(null);
        setMessages([]);
      }
    } catch (err) {
      console.error('Error deleting chat room:', err);
      setError('Failed to delete chat room. Please try again.');
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!message.trim()) return;
    if (!organizationData) {
      setError('Organization data is not available. Please wait until it loads.');
      return;
    }
    if (!user?.attributes?.sub) {
      setError('User information is not available. Please make sure you are logged in.');
      return;
    }

    // Balance check on frontend
    if (balance !== null && balance < 0.50) {
      setError('Insufficient balance. Minimum $0.50 required. Please top up in AI Settings.');
      return;
    }

    const userMessage = message;
    setMessage('');

    // Sending is an explicit user action — re-arm auto-scroll for the new reply
    autoScrollEnabled.current = true;
    setAtBottom(true);
    
    // Add user message to chat
    const userMessageWithContext = {
      role: 'user', 
      content: userMessage,
    };
    
    setMessages(prevMessages => [
      ...prevMessages, 
      userMessageWithContext
    ]);
    
    // Add typing indicator message
    setMessages(prevMessages => [
      ...prevMessages,
      { role: 'assistant', content: '', isTyping: true }
    ]);
    
    setSendingMessage(true);
    
    try {
      // ── Auto-create chat room if none selected ──
      let targetChatRoom = selectedChatRoom;
      if (!targetChatRoom) {
        const createRes = await axios.post(`${API_BASE_URL}/chat-rooms`, {
          name: 'LeanFitt Chat',
          user_id: user.attributes.sub,
          organization_id: activeOrganization?.id
        }, {
          headers: { 'Content-Type': 'application/json', 'X-API-Key': API_KEY }
        });
        targetChatRoom = createRes.data;
        setChatRooms(prev => {
          const exists = prev.some(r => r.id === targetChatRoom.id);
          return exists ? prev : [targetChatRoom, ...prev];
        });
        setSelectedChatRoom(targetChatRoom);
      }

      // ── Build request body for unified endpoint ──
      // Send the user's Cognito sub + admin flag so the backend can scope data:
      // admins get the org-wide view; members (or any "my …" question) see only
      // the projects / reports / action items they own or are a member of.
      const requestBody = {
        content: userMessage,
        role: 'user',
        source: 'LeanFITT',
        organization_id: activeOrganization.id,
        platform_user_id: user?.attributes?.sub,
        is_admin: isOrgAdmin(activeOrganization, user?.attributes?.sub, user?.attributes?.email),
      };

      // Debug log
      console.log('Unified Chat Request:', { chatRoomId: targetChatRoom.id, ...requestBody });

      // ── Abort any previous in-flight request ──
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      const controller = new AbortController();
      abortControllerRef.current = controller;

      // ── Send via fetch for SSE streaming ──
      const response = await fetch(
        `${API_BASE_URL}/chat-rooms/${targetChatRoom.id}/messages?stream=true`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-API-Key': API_KEY,
          },
          body: JSON.stringify(requestBody),
          signal: controller.signal,
        }
      );

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        const apiError = new Error(errData.detail || `Request failed with status ${response.status}`);
        apiError.response = { status: response.status, data: errData };
        throw apiError;
      }

      // ── Stream SSE events ──
      let streamedText = '';

      await parseSSEStream(
        response,
        // onDelta — append text chunk incrementally
        (text) => {
          streamedText += text;
          setMessages(prev => {
            const updated = [...prev];
            const typingIdx = updated.findIndex(m => m.isTyping || m.isStreaming);
            if (typingIdx !== -1) {
              updated[typingIdx] = {
                role: 'assistant',
                content: streamedText,
                isStreaming: true,
              };
            }
            return updated;
          });
        },
        // onDone — finalise the message with metadata
        (doneData) => {
          setMessages(prev => {
            const updated = [...prev];
            const streamIdx = updated.findIndex(m => m.isStreaming || m.isTyping);
            if (streamIdx !== -1) {
              updated[streamIdx] = {
                role: 'assistant',
                content: doneData.full_response || streamedText,
                created_at: doneData.ai_message?.created_at || new Date().toISOString(),
              };
            }
            return updated;
          });

          // Log token usage and update balance immediately from response
          if (doneData.tokens_used && doneData.amount_charged) {
            console.log(`Tokens used: ${doneData.tokens_used}, Cost: $${doneData.amount_charged}, Model: ${doneData.model_used}`);
          }
          if (doneData.new_balance != null) {
            // Use the post-deduction balance from the response — no extra round-trip needed
            setBalance(doneData.new_balance);
            // Broadcast so Navigation header updates immediately
            window.dispatchEvent(new CustomEvent('ai-balance-updated', { detail: { balance: doneData.new_balance } }));
          } else {
            // Fallback: re-fetch if backend didn't return new_balance
            fetchBalance();
          }
        },
        // onError
        (err) => {
          if (err.name !== 'AbortError') {
            console.error('Stream error:', err);
          }
        }
      );

      // Scroll to the end of the AI response only if the user stayed at the bottom
      if (autoScrollEnabled.current) setTimeout(() => scrollToBottom(true), 100);
    } catch (err) {
      if (err?.name === 'AbortError') return; // user cancelled
      console.error('Error sending message:', err);
      
      // Handle specific error types
      let errorMessage = 'Sorry, I encountered an error processing your request. Please try again.';
      
      if (err.response?.status === 402) {
        // Insufficient balance error
        errorMessage = 'Insufficient balance to send message. Please top up your account in AI Settings.';
        setError('Your organization has insufficient balance. Minimum $0.50 required to use chat.');
        fetchBalance(); // refresh balance display
      } else if (err.response?.status === 400) {
        const errorData = err.response.data;
        errorMessage = errorData.detail || 'Invalid request. Please check your input and try again.';
        setError(errorData.detail || 'Invalid request');
      } else if (err.response?.status === 401) {
        errorMessage = 'Authentication failed. Please refresh the page and try again.';
        setError('Authentication error. Please refresh the page.');
      } else {
        setError(`Failed to send message: ${err.message || 'Unknown error'}`);
      }
      
      // Remove typing indicator and add error message
      setMessages(prevMessages => {
        const newMessages = [...prevMessages];
        // Find and remove the typing indicator message
        const typingIndex = newMessages.findIndex(msg => msg.isTyping);
        if (typingIndex !== -1) {
          newMessages.splice(typingIndex, 1);
        }
        
        // Add error message
        newMessages.push({ 
          role: 'system', 
          content: errorMessage
        });
        return newMessages;
      });
    } finally {
      setSendingMessage(false);
    }
  };

  const handleSelectChatRoom = (room) => {
    setSelectedChatRoom(room);
    // Clear current messages immediately for better UX
    setMessages([]);
    fetchChatMessages(room.id);
  };


  // Check if user is admin (owner or co-owner)
  const isUserAdmin = () => {
    if (!user?.attributes?.sub || !activeOrganization) return false;
    
    const isOwner = activeOrganization.owner === user.attributes.sub;
    const isCoOwner = activeOrganization.additionalOwners?.includes(user.attributes.email);
    
    return isOwner || isCoOwner;
  };

  // Navigate to AI settings
  const handleOpenAiSettings = () => {
    navigate('/organization-management', { 
      state: { activeTab: 'ai-settings' } 
    });
  };


  const renderChatMessages = () => {
    return (
      <>
        {messages.map((msg, index) => (
          <div 
            key={index} 
            className={`chat-message ${msg.role === 'user' ? 'chat-message-user' : 'chat-message-assistant'}`}
          >
            <div className="message-avatar">
              {msg.role === 'user' ? (
                <FontAwesomeIcon icon={faUser} className="user-avatar" />
              ) : (
                <Image src={AiIcon} alt="Harmony™ AI" className="assistant-avatar" width="40" height="40" />
              )}
            </div>
            <div className="message-content">
              <div className="message-header">
                <span className="message-sender">
                  {msg.role === 'user' ? 'You' : msg.role === 'system' ? 'System' : 'Harmony™ AI'}
                </span>
                {msg.created_at && (
                  <span className="message-time">
                    {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </span>
                )}
              </div>
              <div className="message-text">
                {msg.role === 'assistant' ? (
                  <div className="response-content">
                    <ReactMarkdown
                      components={{
                        a: ({ href, children }) => {
                          // If href is empty or undefined, render as normal text
                          if (!href || href === '') {
                            return <span>{children}</span>;
                          }
                          // Regular links with valid href
                          return <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>;
                        },
                        ul: ({ children }) => <ul className="chat-list">{children}</ul>,
                        ol: ({ children }) => <ul className="chat-list">{children}</ul>
                      }}
                    >
                      {/* Preprocess content to remove unnecessary nested lists and fix header alignment */}
                      {msg.content
                        .replace(
                          /(<li[^>]*>.*?)\n\s*<ul[^>]*>\s*<li[^>]*>(.*?)<\/li>\s*<\/ul>\s*(<\/li>)/gs,
                          '$1 - $2$3'
                        )
                        .replace(
                          /(<li[^>]*>.*?)\n\s*<ol[^>]*>\s*<li[^>]*>(.*?)<\/li>\s*<\/ol>\s*(<\/li>)/gs,
                          '$1 - $2$3'
                        )
                        .replace(
                          /([^:]*:)\s*\n\n/g,
                          '$1\n\n'
                        )
                      }
                    </ReactMarkdown>
                  </div>
                ) : (
                  <>
                    {msg.content.split('\n').map((line, i) => (
                      <React.Fragment key={i}>
                        {line}
                        {i !== msg.content.split('\n').length - 1 && <br />}
                      </React.Fragment>
                    ))}
                    {/* Show context info for user messages */}
                    {msg.context && (msg.context.report || msg.context.project || msg.context.learning) && (
                      <div className="message-context mt-2">
                        <small className="text-muted">
                          <FontAwesomeIcon icon={faInfoCircle} className="me-1" />
                          Context: 
                          {msg.context.report && <span className="badge bg-primary ms-1">{msg.context.report.name}</span>}
                          {msg.context.project && <span className="badge bg-success ms-1">{msg.context.project.name}</span>}
                          {msg.context.learning && <span className="badge bg-info ms-1">{msg.context.learning.title}</span>}
                        </small>
                      </div>
                    )}
                  </>
                )}
                {msg.isTyping && !msg.content && (
                  <div className="typing-indicator">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                )}
                {msg.isStreaming && (
                  <span className="streaming-cursor">▊</span>
                )}
              </div>
            </div>
          </div>
        ))}
        {/* Scroll target element */}
        <div ref={messagesEndRef} style={{ height: '1px' }} />
      </>
    );
  };

  return (
    <div className="chat-container">
      <Row className="h-100 g-0">
        {/* Sidebar */}
        <Col md={3} className="chat-sidebar">
          <div className="sidebar-header">
            <h4>Chat Rooms</h4>
            <Button 
              variant="primary" 
              size="sm" 
              className="new-chat-btn"
              onClick={() => setShowNewChatModal(true)}
            >
              <FontAwesomeIcon icon={faPlus} /> New Chat
            </Button>
          </div>

          <div className="sidebar-search">
            <InputGroup>
              <InputGroup.Text>
                <FontAwesomeIcon icon={faSearch} />
              </InputGroup.Text>
              <Form.Control
                type="text"
                placeholder="Search chats..."
                className="search-input"
              />
            </InputGroup>
          </div>

          <div className="chat-rooms-list">
            {loadingChatRooms ? (
              <div className="text-center py-4">
                <Spinner animation="border" size="sm" />
                <p className="mt-2">Loading chats...</p>
              </div>
            ) : chatRooms.length === 0 ? (
              <div className="empty-chats">
                <Image src={AiIcon} alt="Harmony™ AI" className="ai-icon" />
                <p>No chat rooms yet</p>
                <Button 
                  variant="outline-primary" 
                  className="start-chat-btn"
                  onClick={() => setShowNewChatModal(true)}
                >
                  Start a new chat
                </Button>
              </div>
            ) : (
              chatRooms.map(room => (
                <div 
                  key={room.id}
                  className={`chat-room-item ${selectedChatRoom?.id === room.id ? 'active' : ''}`}
                  onClick={() => handleSelectChatRoom(room)}
                >
                  <div className="chat-room-icon">
                    <Image src={AiIcon} alt="Harmony™ AI" className="ai-icon" />
                  </div>
                  <div className="chat-room-info">
                    <div className="chat-room-name">
                      {room.name}
                      {room.organization_id === null && (
                        <LegacyDataBadge
                          type="chat"
                          className="ms-2"
                          organizations={organizations}
                          currentOrgId={activeOrganization?.id}
                          onAssign={async (orgId) => {
                            await axios.patch(
                              `${API_BASE_URL}/chat-rooms/${room.id}`,
                              { user_id: user?.attributes?.sub, organization_id: orgId },
                              { headers: { 'Content-Type': 'application/json', 'X-API-Key': API_KEY } }
                            );
                            await fetchChatRooms();
                          }}
                        />
                      )}
                    </div>
                    <div className="chat-room-preview">
                      {room.last_activity ? 
                        `Last activity: ${new Date(room.last_activity).toLocaleDateString()}` : 
                        'Click to view conversation'
                      }
                    </div>
                  </div>
                  <Dropdown className="chat-room-actions">
                    <Dropdown.Toggle variant="link" className="no-arrow">
                      <FontAwesomeIcon icon={faEllipsisV} />
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                      <Dropdown.Item 
                        className="text-danger"
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteChatRoom(room.id);
                        }}
                      >
                        <FontAwesomeIcon icon={faTrash} className="me-2" />
                        Delete
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                </div>
              ))
            )}
          </div>
        </Col>

        {/* Main Chat Area */}
        <Col md={9} className="chat-main">
          {error && (
            <Alert variant="danger" className="chat-alert" onClose={() => setError(null)} dismissible>
              {error}
            </Alert>
          )}

          {!activeOrganization ? (
            <div className="chat-placeholder">
              <FontAwesomeIcon icon={faInfoCircle} size="3x" />
              <h4>Select an Organization</h4>
              <p>Please select an organization to start chatting</p>
            </div>
          ) : checkingPermission ? (
            <div className="chat-loading">
              <Spinner animation="border" role="status" />
              <p>Checking permissions...</p>
            </div>
          ) : !hasPermission ? (
            <div className="chat-placeholder">
              <FontAwesomeIcon icon={faInfoCircle} size="3x" className="text-warning" />
              <h4>Access Restricted</h4>
              <p>You don't have permission to use the AI chat feature.</p>
              <p className="text-muted">Please contact your organization administrator to request access.</p>
            </div>
          ) : loading ? (
            <div className="chat-loading">
              <Spinner animation="border" role="status" />
              <p>Loading chat...</p>
            </div>
          ) : organizationData && (
            <>
              <div className="chat-header">
                <div className="chat-title">
                  <h4>{selectedChatRoom ? selectedChatRoom.name : 'Select a Chat'}</h4>
                  <div className="chat-subtitle">{organizationData.name}</div>
                </div>
                {isUserAdmin() && (
                  <div className="chat-header-actions d-flex align-items-center gap-2">
                    {/* Balance display */}
                    {balance !== null && (
                      <Badge 
                        bg={balance < 0.5 ? 'danger' : balance < 2 ? 'warning' : 'success'} 
                        className="d-flex align-items-center gap-1 py-2 px-3"
                        style={{ fontSize: '0.85rem' }}
                      >
                        <FontAwesomeIcon icon={faWallet} />
                        ${balance.toFixed(2)}
                      </Badge>
                    )}
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={handleOpenAiSettings}
                      title="AI Settings"
                    >
                      <FontAwesomeIcon icon={faCog} className="me-1" />
                      AI Settings
                    </Button>
                  </div>
                )}
              </div>

              <div className="chat-messages" ref={messagesContainerRef} onScroll={handleScroll}>
                {loadingMessages ? (
                  <div className="messages-loading">
                    <Spinner animation="border" role="status" />
                    <p>Loading messages...</p>
                  </div>
                ) : messages.length === 0 ? (
                  <div className="chat-placeholder">
                    <Image src={AiIcon} alt="Harmony™ AI" className="ai-icon" />
                    <h4>Start a Conversation with Harmony™ AI</h4>
                    <p>Ask questions about lean management, process improvement, or organizational tools.</p>
                    <p className="text-muted small">
                      Try asking: "Show me my recent reports", "What are my action items?", or "How many coins do I have?"
                    </p>
                  </div>
                ) : (
                  renderChatMessages()
                )}

                {/* Sticky so it rides the scrollport; height 0 keeps it out of
                    the flow, so it never changes scrollHeight. */}
                {!atBottom && messages.length > 0 && (
                  <div className="jump-to-latest-anchor">
                    <Button
                      size="sm"
                      variant="dark"
                      className="jump-to-latest"
                      onClick={() => scrollToBottom(true)}
                    >
                      <FontAwesomeIcon icon={faArrowDown} className="me-2" />
                      Jump to latest
                    </Button>
                  </div>
                )}
              </div>

              <div className="chat-input-area">
                <Form onSubmit={handleSendMessage} className="chat-form">
                  <InputGroup className="align-items-center">
                    <Form.Control
                      type="text"
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder={organizationData && hasPermission ? "Type your message here..." : "Loading..."}
                      disabled={sendingMessage || !organizationData || !hasPermission}
                      className="chat-input"
                    />
                    <Button 
                      variant="primary" 
                      type="submit" 
                      disabled={sendingMessage || !message.trim() || !organizationData || !hasPermission}
                      className="send-btn"
                    >
                      {sendingMessage ? (
                        <Spinner animation="border" size="sm" />
                      ) : (
                        <FontAwesomeIcon icon={faPaperPlane} />
                      )}
                    </Button>
                  </InputGroup>
                </Form>
                <p className="disclaimer-text mt-2 text-center text-muted small">
                  Note: Harmony™ AI can make mistakes. Always check important info!
                </p>
              </div>
            </>
          )}
        </Col>
      </Row>

      {/* New Chat Modal */}
      <Modal 
        show={showNewChatModal} 
        onHide={() => setShowNewChatModal(false)}
        className="chat-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Create New Harmony™ AI Chat</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group>
              <Form.Label>Chat Room Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter a name for your chat room"
                value={newChatName}
                onChange={(e) => setNewChatName(e.target.value)}
                className="chat-input"
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowNewChatModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="primary"
            onClick={createChatRoom}
            disabled={!newChatName.trim()}
          >
            Create Chat Room
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ChatBot; 